import { IconButton, Label, Stack, Text } from "@fluentui/react";
import React from "react";
import { ITenant } from "../../../data-structures/interfaces";
import { SecuredInput } from "./SecuredInput";

interface IWriteOnlySecretInputProps {
	label: string;
	// Masked, display-only hint of the stored secret (undefined when nothing is stored yet).
	maskedValue?: string;
	propertyName: string;
	// The new value being entered (newTenant[propertyName]); empty unless the admin is replacing.
	propertyValue?: string;
	setNewTenant: (value: React.SetStateAction<ITenant>) => void;
	editButtonText?: string;
	cancelButtonText?: string;
}

/**
 * Write-only secret field. When a secret is already stored it shows the masked hint as a read-only
 * label with an inline icon-only "replace" button; clicking it reveals an empty, no-reveal password
 * input with an inline icon-only "cancel" button (which keeps the stored value). The full secret is
 * never displayed or revealable. When nothing is stored yet, the input is shown for first-time entry.
 */
export const WriteOnlySecretInput: React.FC<IWriteOnlySecretInputProps> = (props: IWriteOnlySecretInputProps) => {
	const hasStored = !!props.maskedValue;
	const [isEditing, setIsEditing] = React.useState<boolean>(false);

	// Clearing the entered value signals "keep the stored secret" on save.
	const clearValue = () => props.setNewTenant(prevTenant => ({ ...prevTenant, [props.propertyName]: null }));

	const startEditing = () => {
		clearValue();
		setIsEditing(true);
	};

	const cancelEditing = () => {
		clearValue();
		setIsEditing(false);
	};

	if (hasStored && !isEditing) {
		const replaceLabel = props.editButtonText ?? "Replace";
		return (
			<Stack tokens={{ childrenGap: 2 }}>
				<Label>{props.label}</Label>
				<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
					<Text styles={{ root: { fontFamily: "monospace" } }}>{props.maskedValue}</Text>
					<IconButton
						iconProps={{ iconName: "Edit" }}
						title={replaceLabel}
						ariaLabel={replaceLabel}
						onClick={startEditing}
					/>
				</Stack>
			</Stack>
		);
	}

	const cancelLabel = props.cancelButtonText ?? "Cancel";
	return (
		<Stack horizontal verticalAlign="end" tokens={{ childrenGap: 4 }}>
			<Stack.Item grow>
				<SecuredInput
					inputLabel={props.label}
					propertyName={props.propertyName}
					propertyValue={props.propertyValue}
					hideReveal
					setNewTenant={props.setNewTenant}
				/>
			</Stack.Item>
			{hasStored && (
				<IconButton
					iconProps={{ iconName: "Cancel" }}
					title={cancelLabel}
					ariaLabel={cancelLabel}
					onClick={cancelEditing}
				/>
			)}
		</Stack>
	);
};
